import { PICK_PACK_SALES_ORG, USER_ROLE } from '@/constants/const';
import { ROUTES_URL } from '@/constants/routesConst';
import type { SalesOrgItem, UserSalesOrganisationList } from '@/types/login';

export type PostLoginLandingRouteParams = {
    isEnableKRExpansionPickAndPack: boolean;
    userRole?: string;
    salesOrgAssignments?: UserSalesOrganisationList['value'];
};

export type PickPackLandingRouteParams = {
    isEnableKRExpansionPickAndPack: boolean;
    userRole?: string | null;
    selectedSalesOrg?: string | null;
    salesOrgAssignments?: readonly SalesOrgItem[] | null;
};

export type PickPackSalesOrgConfigurationAccessParams = PickPackLandingRouteParams;

export type PickPackAccessParams = Omit<PickPackLandingRouteParams, 'isEnableKRExpansionPickAndPack'> & {
    isPickPackFeatureEnabled: boolean;
};

export type PickPackAccess = {
    isPickPackFeatureEnabled: boolean;
    isShipToRestrictedRole: boolean;
    isShipToRestricted: boolean;
    isPickPackFlow: boolean;
    isPickPackClient: boolean;
    landingRoute: string | null;
};

type PickPackRoleCapabilities = {
    pickPack: boolean;
    shipToRestricted: boolean;
    landingRoute: string | null;
};

const ROLE_CAPABILITIES: Readonly<Record<string, PickPackRoleCapabilities>> = {
    [USER_ROLE.CLIENT_USER]: {
        pickPack: true,
        shipToRestricted: true,
        landingRoute: ROUTES_URL.PICK_PACK_BULK_ORDER_UPLOAD
    },
    [USER_ROLE.CLIENT_ADMIN]: {
        pickPack: true,
        shipToRestricted: true,
        landingRoute: ROUTES_URL.PICK_PACK_BULK_ORDER_UPLOAD
    },
    [USER_ROLE.ROOT_ADMIN]: {
        pickPack: true,
        shipToRestricted: true,
        landingRoute: ROUTES_URL.SALES_ORG_CONFIG
    },
    [USER_ROLE.ZP_ADMIN]: {
        pickPack: true,
        shipToRestricted: true,
        landingRoute: ROUTES_URL.SALES_ORG_CONFIG
    },
    [USER_ROLE.INTERNAL_SALES_REP]: {
        pickPack: false,
        shipToRestricted: true,
        landingRoute: null
    },
    [USER_ROLE.EXTERNAL_SALES_REP]: {
        pickPack: false,
        shipToRestricted: true,
        landingRoute: null
    }
};

export const isPickPackRole = (userRole?: string | null): boolean => {
    return typeof userRole === 'string' && ROLE_CAPABILITIES[userRole]?.pickPack === true;
};

export const isPickPackShipToRestrictedRole = (userRole?: string | null): boolean => {
    return typeof userRole === 'string' && ROLE_CAPABILITIES[userRole]?.shipToRestricted === true;
};

export const isPickPackClientRole = (userRole?: string | null): boolean => {
    return (
        typeof userRole === 'string' &&
        ROLE_CAPABILITIES[userRole]?.landingRoute === ROUTES_URL.PICK_PACK_BULK_ORDER_UPLOAD
    );
};

const getUniqueSalesOrgCodes = (salesOrgAssignments?: readonly SalesOrgItem[] | null): string[] => {
    const salesOrgCodes =
        salesOrgAssignments
            ?.map((assignment) => assignment.salesOrg || assignment.SalesOrg || assignment.value)
            .filter(
                (salesOrgCode): salesOrgCode is string => typeof salesOrgCode === 'string' && salesOrgCode !== ''
            ) ?? [];

    return [...new Set(salesOrgCodes)];
};

const getPickPackLandingRouteByRole = (userRole?: string | null): string | null => {
    return userRole ? ROLE_CAPABILITIES[userRole]?.landingRoute ?? null : null;
};

export const hasPickPackBulkUploadAccess = (
    role: string | undefined,
    salesOrg: string | undefined,
    salesOrgAssignments?: readonly SalesOrgItem[] | null,
    isEnableKRExpansionPickAndPack = false
): boolean => {
    return (
        getPickPackLandingRoute({
            isEnableKRExpansionPickAndPack,
            userRole: role,
            selectedSalesOrg: salesOrg,
            salesOrgAssignments
        }) === ROUTES_URL.PICK_PACK_BULK_ORDER_UPLOAD
    );
};

export const getPickPackLandingRoute = ({
    isEnableKRExpansionPickAndPack,
    userRole,
    selectedSalesOrg,
    salesOrgAssignments
}: PickPackLandingRouteParams): string | null => {
    const assignedSalesOrgCodes = getUniqueSalesOrgCodes(salesOrgAssignments);

    if (
        !isEnableKRExpansionPickAndPack ||
        selectedSalesOrg !== PICK_PACK_SALES_ORG ||
        !assignedSalesOrgCodes.includes(PICK_PACK_SALES_ORG)
    ) {
        return null;
    }

    return getPickPackLandingRouteByRole(userRole);
};

export const getPickPackAccess = ({
    isPickPackFeatureEnabled,
    userRole,
    selectedSalesOrg,
    salesOrgAssignments
}: PickPackAccessParams): PickPackAccess => {
    const isShipToRestrictedRole = isPickPackShipToRestrictedRole(userRole);

    return {
        isPickPackFeatureEnabled,
        isShipToRestrictedRole,
        isShipToRestricted: isPickPackFeatureEnabled && isShipToRestrictedRole,
        isPickPackFlow: isPickPackFeatureEnabled && isPickPackRole(userRole),
        isPickPackClient: isPickPackFeatureEnabled && isPickPackClientRole(userRole),
        landingRoute: getPickPackLandingRoute({
            isEnableKRExpansionPickAndPack: isPickPackFeatureEnabled,
            userRole,
            selectedSalesOrg,
            salesOrgAssignments
        })
    };
};

export const hasPickPackSalesOrgConfigurationAccess = ({
    isEnableKRExpansionPickAndPack,
    userRole,
    selectedSalesOrg,
    salesOrgAssignments
}: PickPackSalesOrgConfigurationAccessParams): boolean => {
    return (
        getPickPackLandingRoute({
            isEnableKRExpansionPickAndPack,
            userRole,
            selectedSalesOrg,
            salesOrgAssignments
        }) ===
        ROUTES_URL.SALES_ORG_CONFIG
    );
};

export const getPostLoginLandingRoute = ({
    isEnableKRExpansionPickAndPack,
    userRole,
    salesOrgAssignments
}: PostLoginLandingRouteParams): string => {
    if (!isEnableKRExpansionPickAndPack) return ROUTES_URL.HOME;

    const assignedSalesOrgCodes = getUniqueSalesOrgCodes(salesOrgAssignments);
    if (assignedSalesOrgCodes.length !== 1) return ROUTES_URL.HOME;

    return (
        getPickPackLandingRoute({
            isEnableKRExpansionPickAndPack,
            userRole,
            selectedSalesOrg: assignedSalesOrgCodes[0],
            salesOrgAssignments
        }) ?? ROUTES_URL.HOME
    );
};
